import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';

import { styled } from '@mui/material/styles';
import {
  Person,
  Email,
  Grade,
  Business,
  Phone,
  Numbers,
  Info,
  Save,
  Edit,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const ProfileContainer = styled(Box)`
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 2rem 0;
`;

const ProfileCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 1rem;
  
  .MuiOutlinedInput-root {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background: rgba(255, 255, 255, 0.9);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
    }
  }
`;

const ProfilePage: React.FC = () => {
  const { student, updateProfile, isLoading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    grade: 1,
    school: '',
    schoolRegistrationNumber: '',
    phone: '',
    parentName: '',
    parentPhone: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (student) {
      setFormData({
        name: student.name || '',
        email: student.email || '',
        grade: student.grade || 1,
        school: student.school || '',
        schoolRegistrationNumber: student.schoolRegistrationNumber || '',
        phone: student.phone || '',
        parentName: student.parentName || '',
        parentPhone: student.parentPhone || '',
      });
    }
  }, [student]);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'grade' ? Number(e.target.value) : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      await updateProfile(formData);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    }
  };

  const handleCancel = () => {
    if (student) {
      setFormData({
        name: student.name || '',
        email: student.email || '',
        grade: student.grade || 1,
        school: student.school || '',
        schoolRegistrationNumber: student.schoolRegistrationNumber || '',
        phone: student.phone || '',
        parentName: student.parentName || '',
        parentPhone: student.parentPhone || '',
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  if (!student) {
    return (
      <ProfileContainer sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
        <Container maxWidth="lg">
        <ProfileCard>
                      <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={4}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.8rem',
                  mr: 3,
                }}
              >
                {student.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box flex={1}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {student.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Grade {student.grade} • {student.school}
                </Typography>
              </Box>
              <Button
                variant={isEditing ? "outlined" : "contained"}
                startIcon={isEditing ? <Save /> : <Edit />}
                onClick={() => setIsEditing(!isEditing)}
                disabled={isLoading}
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            {/* Profile Form */}
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="h6" fontWeight={600} mb={2} color="primary">
                Personal Information
              </Typography>

                             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                 <StyledTextField
                   fullWidth
                   label="Full Name"
                   value={formData.name}
                   onChange={handleInputChange('name')}
                   disabled={!isEditing || isLoading}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Person sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                   }}
                   required
                 />
                 <StyledTextField
                   fullWidth
                   label="Email Address"
                   value={formData.email}
                   disabled={true} // Email should not be editable
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Email sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                   }}
                 />
               </Box>

              <Typography variant="h6" fontWeight={600} mb={2} color="primary">
                Academic Information
              </Typography>

                             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                 <StyledTextField
                   fullWidth
                   label="Grade"
                   type="number"
                   value={formData.grade}
                   onChange={handleInputChange('grade')}
                   disabled={!isEditing || isLoading}
                   inputProps={{ min: 1, max: 12 }}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Grade sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                   }}
                   required
                 />
                 <StyledTextField
                   fullWidth
                   label="School Name"
                   value={formData.school}
                   onChange={handleInputChange('school')}
                   disabled={!isEditing || isLoading}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Business sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                   }}
                   required
                 />
                 <StyledTextField
                   fullWidth
                   label="School Registration Number"
                   value={formData.schoolRegistrationNumber}
                   onChange={handleInputChange('schoolRegistrationNumber')}
                   disabled={!isEditing || isLoading}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Numbers sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                     endAdornment: (
                       <InputAdornment position="end">
                         <Tooltip 
                           title="Registration number with education boards like GSEB, CBSE, NCERT, etc."
                           placement="top"
                         >
                           <Info sx={{ color: 'text.secondary', cursor: 'help' }} />
                         </Tooltip>
                       </InputAdornment>
                     ),
                   }}
                   placeholder="e.g., GSEB123456, CBSE789012"
                   required
                 />
               </Box>

               <Typography variant="h6" fontWeight={600} mb={2} color="primary">
                 Contact Information
               </Typography>

               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                 <StyledTextField
                   fullWidth
                   label="Your Phone Number"
                   value={formData.phone}
                   onChange={handleInputChange('phone')}
                   disabled={!isEditing || isLoading}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Phone sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                   }}
                   placeholder="10-digit number"
                   required
                 />
                 <StyledTextField
                   fullWidth
                   label="Parent/Guardian Name"
                   value={formData.parentName}
                   onChange={handleInputChange('parentName')}
                   disabled={!isEditing || isLoading}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Person sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                   }}
                   required
                 />
                 <StyledTextField
                   fullWidth
                   label="Parent/Guardian Phone"
                   value={formData.parentPhone}
                   onChange={handleInputChange('parentPhone')}
                   disabled={!isEditing || isLoading}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <Phone sx={{ color: 'text.secondary' }} />
                       </InputAdornment>
                     ),
                   }}
                   placeholder="10-digit number"
                   required
                 />
               </Box>

              {/* Action Buttons */}
              {isEditing && (
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress size={20} /> : <Save />}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </ProfileCard>
      </Container>
    </ProfileContainer>
  );
};

export default ProfilePage; 